import React, { ChangeEvent, useId, useState } from 'react';
import cx from 'classnames';
import Image from 'next/legacy/image';

import { BaseInputProps, TextInputType, TextInputWidth } from '../shared';

import icon from '@/images/error_icon.svg';
import iconEyeHide from '@/images/ic_eye_hide_filled.svg';
import iconEyeShow from '@/images/ic_eye_show_filled.svg';

interface TextInputProps extends BaseInputProps {
  borderColor?: string;
  handleOnChange: (event: ChangeEvent<HTMLInputElement>, value: string) => void;
}

export function TextInput({
  value,
  type,
  label,
  placeholder,
  error,
  required,
  handleOnChange,
  disabled = false,
  width = TextInputWidth.FULL,
  borderColor = '#00D9B4',
  inputMode,
  pattern,
  minLength,
  maxLength,
  autoComplete
}: TextInputProps) {
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const inputId = useId();
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const float = !!value || isFocused;

  return (
    <>
      <div
        className={cx(
          'text-input-wrapper',
          'relative h-16 rounded-input-box-shape p-0 transition duration-300',
          'border',
          error ? 'border-hibiscus' : null
        )}
        style={{
          width: `${width}%`,
          borderColor: error ? undefined : borderColor,
          marginBottom: error ? '8px' : '16px'
        }}
      >
        <label
          className={cx(
            'absolute rounded-input-box-shape font-medium transition-all duration-300',
            float
              ? 'top-0 -mt-2 ml-4 translate-y-0 bg-white px-1 text-[0.8em]'
              : 'top-1/2 -translate-y-1/2 pl-4 text-[1em]'
          )}
          htmlFor={inputId}
        >
          {label}
          {required ? '*' : ''}
        </label>
        <input
          id={inputId}
          type={showPassword ? TextInputType.TEXT : type}
          value={value}
          placeholder={placeholder}
          disabled={disabled}
          required={required}
          inputMode={inputMode}
          pattern={pattern}
          autoComplete={autoComplete ?? 'off'}
          min="0"
          minLength={minLength || 1}
          maxLength={maxLength || 320}
          onChange={(e: ChangeEvent<HTMLInputElement>) => {
            handleOnChange?.(e, e.target.value);
          }}
          onFocus={() => {
            setIsFocused(true);
          }}
          onBlur={() => {
            setIsFocused(false);
          }}
          aria-required={required}
          aria-invalid={!!error}
          data-testid={`input-${type}-${label}`}
          className={cx(
            'bg-transparent h-full w-full rounded-input-box-shape border-0 outline-none transition',
            'px-4 text-[1em] leading-[1.5]',
            'disabled:text-dark-gray',
            float ? 'placeholder:opacity-100' : 'placeholder:opacity-0'
          )}
        />
        {type === TextInputType.PASSWORD && (
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            data-testid={`input-${type}-${label}-btn-${showPassword}`}
            className="absolute right-4 top-1/2 h-6 w-6 -translate-y-1/2 cursor-pointer border-none bg-[length:100%_100%]"
            style={{
              backgroundImage: `url(${
                showPassword ? iconEyeShow.src : iconEyeHide.src
              })`
            }}
          />
        )}
      </div>
      <div
        className={cx(
          'error-message flex items-center overflow-hidden text-hibiscus',
          error ? 'mb-4 max-h-[24px] opacity-100' : 'mb-0 max-h-0 opacity-0'
        )}
        data-testid={`input-${type}-${label}-error`}
      >
        <div className="ml-4 mr-2 flex h-full">
          <Image src={icon} alt="Form input error" />
        </div>
        {error || ''}
      </div>
    </>
  );
}
